import { Routes } from '@angular/router';
import { authGuard } from './guards/auth.guard';
import { forgotPasswordGuard } from './guards/forgot-password.guard';
import { loggedOffGuard } from './guards/logged-off.guard';

export const routes: Routes = [
  {
    path: '',
    redirectTo: 'home',
    pathMatch: 'full',
  },
  {
    // Deep link to order directly for logged in users
    path: 'order',
    loadComponent: () =>
      import('./pages/order-deep-link/order-deep-link.page').then(
        (m) => m.OrderDeepLinkPage
      ),
  },
  {
    path: 'login',
    loadComponent: () => import('./pages/login/login.page').then((m) => m.LoginPage),
    canActivate: [loggedOffGuard],
  },
  {
    path: 'forgot-password',
    loadComponent: () =>
      import('./pages/forgot-password/forgot-password.page').then(
        (m) => m.ForgotPasswordPage
      ),
    canActivate: [forgotPasswordGuard],
  },
  /**
   * Onboarding pages
   * -----------------------------------------------------
   */
  {
    path: 'onboarding',
    redirectTo: 'onboarding/welcome',
    pathMatch: 'full',
  },
  {
    path: 'onboarding/welcome',
    loadComponent: () =>
      import('./pages/onboarding/welcome/welcome.page').then((m) => m.WelcomePage),
    canActivate: [loggedOffGuard],
  },
  {
    path: 'onboarding/dropoff-pickup-point',
    loadComponent: () =>
      import('./pages/onboarding/dropoff-pickup-point/dropoff-pickup-point.page').then(
        (m) => m.DropoffPickupPointPage
      ),
    canActivate: [loggedOffGuard],
  },
  {
    path: 'onboarding/register',
    loadComponent: () =>
      import('./pages/onboarding/register/register.page').then((m) => m.RegisterPage),
    canActivate: [loggedOffGuard],
  },

  /**
   * Main menu pages
   * -----------------------------------------------------
   */
  {
    path: 'home',
    loadComponent: () => import('./pages/home/home.page').then((m) => m.HomePage),
    canActivate: [authGuard],
  },
  {
    path: 'where-to-dropoff-laundry',
    loadComponent: () =>
      import('./pages/where-to-dropoff-laundry/where-to-dropoff-laundry.page').then(
        (m) => m.WhereToDropoffLaundryPage
      ),
  },
  {
    path: 'faq',
    loadComponent: () => import('./pages/faq/faq.page').then((m) => m.FaqPage),
    canActivate: [authGuard],
  },
  {
    path: 'orders',
    loadComponent: () => import('./pages/orders/orders.page').then((m) => m.OrdersPage),
    canActivate: [authGuard],
  },
  {
    path: 'orders/:id',
    loadComponent: () =>
      import('./pages/home/order-details/order-details.page').then(
        (m) => m.OrderDetailsPage
      ),
    canActivate: [authGuard],
  },
  {
    path: 'payments-and-invoices',
    loadComponent: () =>
      import('./pages/payments-and-invoices/payments-and-invoices.page').then(
        (m) => m.PaymentsAndInvoicesPage
      ),
    canActivate: [authGuard],
  },
  {
    path: 'payments-and-invoices/:id',
    loadComponent: () =>
      import(
        './pages/payments-and-invoices-details/payments-and-invoices-details.page'
      ).then((m) => m.PaymentsAndInvoicesDetailsPage),
    canActivate: [authGuard],
  },
  {
    path: 'houses',
    loadComponent: () => import('./pages/houses/houses.page').then((m) => m.HousesPage),
    canActivate: [authGuard],
  },
  {
    path: 'houses/add',
    loadComponent: () =>
      import('./pages/house-details/house-details.page').then(
        (m) => m.HouseDetailsPage
      ),
    canActivate: [authGuard],
    data: { isCreation: true },
  },
  {
    path: 'houses/:id',
    loadComponent: () =>
      import('./pages/house-details/house-details.page').then(
        (m) => m.HouseDetailsPage
      ),
    canActivate: [authGuard],
  },
  {
    path: 'contact',
    loadComponent: () =>
      import('./pages/contact/contact.page').then((m) => m.ContactPage),
    canActivate: [authGuard],
  },
  /**
   * Account pages
   * -----------------------------------------------------
   */
  {
    path: 'account',
    loadComponent: () =>
      import('./pages/account/account.page').then((m) => m.AccountPage),
    canActivate: [authGuard],
  },
  {
    path: 'account/payment-methods',
    loadComponent: () =>
      import('./pages/account/payment-methods/payment-methods.page').then(
        (m) => m.PaymentMethodsPage
      ),
    canActivate: [authGuard],
  },
  {
    path: 'account/personal-details',
    loadComponent: () =>
      import('./pages/account/personal-details/personal-details.page').then(
        (m) => m.PersonalInfosPage
      ),
    canActivate: [authGuard],
  },
  {
    path: 'account/credits',
    loadComponent: () =>
      import('./pages/credits/credits-list/credits-list.page').then(
        (m) => m.CreditsListPage
      ),
    canActivate: [authGuard],
  },
  {
    path: 'account/credits/:id',
    loadComponent: () =>
      import('./pages/credits/credit-details/credit-details.page').then(
        (m) => m.CreditDetailsPage
      ),
    canActivate: [authGuard],
  },
  {
    path: 'account/referral',
    loadComponent: () =>
      import('./pages/referral/referral.page').then((m) => m.ReferralPage),
    canActivate: [authGuard],
  },
  {
    path: 'account/change-password',
    loadComponent: () =>
      import('./pages/account/change-pwd/change-pwd.page').then((m) => m.ChangePwdPage),
    canActivate: [authGuard],
  },
  /**
   * New order pages
   * -----------------------------------------------------
   */
  {
    path: 'new-order/identify-my-kit',
    loadComponent: () =>
      import('./pages/new-order/identify-my-kit/identify-my-kit.page').then(
        (m) => m.IdentifyMyKitPage
      ),
    canActivate: [authGuard],
  },
  {
    path: 'new-order/print-order-label',
    loadComponent: () =>
      import('./pages/new-order/print/print.page').then((m) => m.PrintPage),
    canActivate: [authGuard],
  },
  {
    path: 'new-order/get-bag',
    loadComponent: () =>
      import('./pages/new-order/get-bag/get-bag.page').then((m) => m.GetBagPage),
    canActivate: [authGuard],
  },
  {
    path: 'new-order/receive-at-home',
    loadComponent: () =>
      import('./pages/new-order/receive-at-home/receive-at-home.page').then(
        (m) => m.ReceiveAtHomePage
      ),
    canActivate: [authGuard],
  },
  {
    path: 'new-order/create-cart',
    loadComponent: () =>
      import('./pages/new-order/create-cart/create-cart.page').then(
        (m) => m.CreateCartPage
      ),
    canActivate: [authGuard],
  },
  {
    path: 'new-order/order-confirmation',
    loadComponent: () =>
      import('./pages/new-order/order-confirmation/order-confirmation.page').then(
        (m) => m.OrderConfirmationPage
      ),
    canActivate: [authGuard],
  },
  {
    path: '**',
    redirectTo: 'home',
  },
];
